import { useState } from 'react';

const catalogItems = [
  {
    id: 1,
    title: '광센서 스마트 디지털 수도미터 카탈로그',
    description: '3세대 광센서 스마트 수도미터기의 제품 사양, 기술 특징, 설치 가이드를 포함한 종합 카탈로그입니다.',
    icon: 'ri-file-pdf-2-line',
    pages: '24페이지',
    size: '8.5MB',
    tag: '제품 카탈로그',
    gradient: 'from-teal-600 to-cyan-600',
  },
  {
    id: 2,
    title: '기술 사양서 및 인증서',
    description: '광학 센싱 기술 상세 사양, 특허 정보, KC 인증 및 각종 시험 성적서를 포함한 기술 문서입니다.',
    icon: 'ri-file-text-line',
    pages: '16페이지',
    size: '5.2MB',
    tag: '기술 문서',
    gradient: 'from-cyan-600 to-teal-600',
  },
  {
    id: 3,
    title: '사업 제안서 (지자체·수도사업소)',
    description: '지자체 및 수도사업소를 위한 스마트 수도미터 도입 제안서로, 비용 절감 효과와 도입 사례를 포함합니다.',
    icon: 'ri-slideshow-line',
    pages: '32페이지',
    size: '12.3MB',
    tag: '사업 제안서',
    gradient: 'from-teal-700 to-emerald-600',
  },
];

export default function CatalogDownload() {
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedCatalog, setSelectedCatalog] = useState<typeof catalogItems[0] | null>(null);
  const [formData, setFormData] = useState({ name: '', company: '', email: '', phone: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleDownloadClick = (catalog: typeof catalogItems[0]) => {
    setSelectedCatalog(catalog);
    setShowModal(true);
    setSubmitted(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCatalog) return;

    setDownloadingId(selectedCatalog.id);

    try {
      const body = new URLSearchParams();
      body.append('name', formData.name);
      body.append('company', formData.company);
      body.append('email', formData.email);
      body.append('phone', formData.phone);
      body.append('catalog', selectedCatalog.title);

      await fetch('https://readdy.ai/api/form/d648iqdanburc340qq70', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      setSubmitted(true);
      setTimeout(() => {
        setDownloadingId(null);
        setShowModal(false);
        setFormData({ name: '', company: '', email: '', phone: '' });
        setSubmitted(false);
      }, 2500);
    } catch {
      setDownloadingId(null);
    }
  };

  return (
    <section id="catalog" className="py-16 md:py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10 md:mb-16">
          <div className="inline-block px-5 py-2 bg-teal-50 border border-teal-200 rounded-full mb-4">
            <span className="text-teal-600 text-xs md:text-sm font-bold uppercase tracking-wider">Catalog</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-gray-900 mb-4 md:mb-6">
            제품 카탈로그
          </h2>
          <p className="text-sm md:text-lg text-gray-600 max-w-3xl mx-auto font-medium">
            로보에셋그리드의 스마트 수도미터기 제품 정보를 확인하세요
          </p>
        </div>

        {/* Catalog Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {catalogItems.map((catalog) => (
            <div
              key={catalog.id}
              className="group relative bg-white rounded-2xl border border-gray-200 hover:border-teal-300 transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 overflow-hidden"
            >
              {/* 상단 그라데이션 바 */}
              <div className={`h-1.5 bg-gradient-to-r ${catalog.gradient}`}></div>

              <div className="p-6 md:p-8">
                {/* 태그 */}
                <div className="flex items-center justify-between mb-4 md:mb-6">
                  <span className="px-3 py-1 bg-teal-50 text-teal-600 text-[10px] md:text-xs font-semibold rounded-full">
                    {catalog.tag}
                  </span>
                  <div className="flex items-center gap-2 text-[10px] md:text-xs text-gray-400">
                    <span>{catalog.pages}</span>
                    <span>·</span>
                    <span>{catalog.size}</span>
                  </div>
                </div>

                {/* 아이콘 */}
                <div className={`w-14 md:w-18 h-14 md:h-18 bg-gradient-to-br ${catalog.gradient} rounded-xl md:rounded-2xl flex items-center justify-center mb-4 md:mb-6 shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-500`}>
                  <i className={`${catalog.icon} text-2xl md:text-3xl text-white`}></i>
                </div>

                {/* 내용 */}
                <h3 className="text-xl md:text-2xl font-black text-gray-900 mb-3 md:mb-4 group-hover:text-teal-600 transition-colors duration-300 leading-snug">
                  {catalog.title}
                </h3>
                <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-5 md:mb-6 font-medium">
                  {catalog.description}
                </p>

                {/* 다운로드 버튼 */}
                <button
                  onClick={() => handleDownloadClick(catalog)}
                  disabled={downloadingId === catalog.id}
                  className={`w-full flex items-center justify-center gap-2 px-5 py-3 md:py-3.5 bg-gradient-to-r ${catalog.gradient} text-white text-sm font-semibold rounded-xl hover:shadow-lg transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-70 disabled:cursor-not-allowed`}
                >
                  {downloadingId === catalog.id ? (
                    <>
                      <i className="ri-loader-4-line text-lg animate-spin"></i>
                      <span>다운로드 준비 중...</span>
                    </>
                  ) : (
                    <>
                      <i className="ri-download-2-line text-lg"></i>
                      <span>카탈로그 다운로드</span>
                    </>
                  )}
                </button>
              </div>

              {/* 호버 글로우 */}
              <div className={`absolute -bottom-16 -right-16 w-32 h-32 bg-gradient-to-br ${catalog.gradient} rounded-full blur-3xl opacity-0 group-hover:opacity-10 transition-all duration-500`}></div>
            </div>
          ))}
        </div>

        {/* 하단 안내 */}
        <div className="mt-10 md:mt-14 text-center">
          <div className="inline-flex items-center gap-2 px-5 py-3 bg-gray-50 rounded-full border border-gray-200">
            <i className="ri-information-line text-teal-600 text-lg"></i>
            <span className="text-xs md:text-sm text-gray-600">
              카탈로그 다운로드 시 간단한 정보 입력이 필요합니다. 입력하신 정보는 자료 발송 목적으로만 사용됩니다.
            </span>
          </div>
        </div>
      </div>

      {/* 다운로드 모달 */}
      {showModal && selectedCatalog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => { setShowModal(false); setSubmitted(false); }}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 md:p-8 animate-[fadeInUp_0.3s_ease-out]">
            {/* 닫기 버튼 */}
            <button
              onClick={() => { setShowModal(false); setSubmitted(false); }}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>

            {submitted ? (
              <div className="text-center py-6">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-3xl text-teal-600"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">다운로드가 시작됩니다</h3>
                <p className="text-sm text-gray-600">
                  입력하신 이메일로도 카탈로그를 발송해 드립니다.
                </p>
              </div>
            ) : (
              <>
                {/* 모달 헤더 */}
                <div className="mb-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-10 h-10 bg-gradient-to-br ${selectedCatalog.gradient} rounded-lg flex items-center justify-center`}>
                      <i className={`${selectedCatalog.icon} text-lg text-white`}></i>
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-gray-900">{selectedCatalog.title}</h3>
                      <p className="text-xs text-gray-500">{selectedCatalog.pages} · {selectedCatalog.size}</p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500">다운로드를 위해 아래 정보를 입력해 주세요.</p>
                </div>

                {/* 폼 */}
                <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form id="catalog-download-form">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">이름 <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="홍길동"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition-colors"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">회사/기관명</label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      placeholder="소속 회사 또는 기관명"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">이메일 <span className="text-red-500">*</span></label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="example@company.com"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition-colors"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">연락처</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="010-0000-0000"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition-colors"
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-gradient-to-r from-teal-600 to-cyan-600 text-white text-sm font-semibold rounded-xl hover:shadow-lg transition-all duration-300 cursor-pointer whitespace-nowrap mt-2"
                  >
                    <i className="ri-download-2-line text-lg"></i>
                    <span>카탈로그 다운로드</span>
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </section>
  );
}
